import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import "./FileMapTab.scss";

const W          = 800;
const H          = 500;
const CX         = W / 2;
const CY         = H / 2;
const MIN_NODE_R = 16;
const MAX_NODE_R = 40;
const OWNER_R    = 52;

function bloomR(count) {
  return Math.max(160, Math.min(220, 140 + count * 5));
}

const SENSITIVITY_META = {
  CRITICAL:      { label: "Critical",       chipCls: "chip--critical", fill: "#E22D1B", light: "#febbcb", text: "#ff2251" },
  HIGH:          { label: "High",           chipCls: "chip--high",     fill: "#ff2251", light: "#febbcb", text: "#ff2251" },
  MEDIUM:        { label: "Medium",         chipCls: "chip--medium",   fill: "#ff8421", light: "#fde5d3", text: "#ff8421" },
  LOW:           { label: "Low",            chipCls: "chip--low",      fill: "#081981", light: "#A8D5FF", text: "#081981" },
  NOT_PROCESSED: { label: "Pending",        chipCls: "chip--pending",  fill: "#6B7280", light: "#F1EFE8", text: "#5F5E5A" },
  NO_SENSITIVITY:{ label: "No sensitivity", chipCls: "chip--none",     fill: "#888780", light: "#F1EFE8", text: "#888780" },
};

function sensitivityMeta(cat) {
  return SENSITIVITY_META[cat] ?? SENSITIVITY_META.NO_SENSITIVITY;
}

function formatSize(bytes = 0) {
  if (bytes < 1024)           return `${bytes} B`;
  if (bytes < 1024 * 1024)   return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3)     return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function fileInitial(name = "") {
  return (name.trim()[0] ?? "?").toUpperCase();
}


function getInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join("");
}

function toFileRow(item) {
  return {
    id:               item.originalFileId,
    fileName:         item.fileName,
    category:         item.category,
    duplicateCount:   item.duplicateCount,
    duplicateUserCount: item.duplicateUserCount,
    totalSize:        item.totalSize,
  };
}


function computeLayout(files, drive) {
  const maxDup = Math.max(...files.map((f) => f.duplicateCount), 1);
  const BR     = bloomR(files.length);

  function nodeR(dc) {
    return MIN_NODE_R + (dc / maxDup) * (MAX_NODE_R - MIN_NODE_R);
  }

  const fileNodes = files.map((item, i) => {
    const angle = (2 * Math.PI / files.length) * i - Math.PI / 2;
    const r     = nodeR(item.duplicateCount);
    const meta  = sensitivityMeta(item.category);
    return {
      id:       item.originalFileId,
      label:    fileInitial(item.fileName),
      fileName: item.fileName,
      category: item.category,
      duplicateCount:    item.duplicateCount,
      duplicateUserCount: item.duplicateUserCount,
      totalSize: item.totalSize,
      role:  "file",
      r,
      fill:  meta.fill,
      meta,
      ox: Math.cos(angle) * BR,
      oy: Math.sin(angle) * BR,
      x:  CX + Math.cos(angle) * BR,
      y:  CY + Math.sin(angle) * BR,
      fx: CX + Math.cos(angle) * BR,
      fy: CY + Math.sin(angle) * BR,
      item,
    };
  });

  const totalDupObjects = files.reduce((s, f) => s + f.duplicateCount, 0);
  const ownerNode = {
    id:       "owner",
    label:    getInitials(drive?.driveName ?? ""),
    fileName: drive?.driveName ?? "Drive",
    role:     "owner",
    r:        OWNER_R,
    fill:     "#534AB7",
    duplicateCount: totalDupObjects,
    ox: 0, oy: 0,
    x: CX, y: CY,
    fx: CX, fy: CY,
  };

  return { ownerNode, fileNodes };
}


function createTooltip(container) {
  const tt = document.createElement("div");
  tt.className = "fmap-tooltip";
  tt.style.cssText = `
    position:absolute;pointer-events:none;opacity:0;
    transition:opacity 0.15s;z-index:10;
  `;
  container.appendChild(tt);
  return tt;
}

function showTooltip(tt, node, containerRect, svgPoint) {
  const meta = sensitivityMeta(node.category);

  tt.innerHTML = `
    <div class="fmap-tt__header">
      <span class="fmap-tt__initial" style="background:${meta.fill}">${fileInitial(node.fileName)}</span>
      <span class="fmap-tt__name" title="${node.fileName}">${node.fileName}</span>
    </div>
    <div class="fmap-tt__chip chip--${meta.chipCls.replace("chip--","")}" style="background:${meta.light};color:${meta.text}">
      ${meta.label}
    </div>
    <div class="fmap-tt__rows">
      <div class="fmap-tt__row">
        <span class="fmap-tt__key">Duplicate copies</span>
        <span class="fmap-tt__val">${node.duplicateCount}</span>
      </div>
      <div class="fmap-tt__row">
        <span class="fmap-tt__key">Affected users</span>
        <span class="fmap-tt__val">${node.duplicateUserCount}</span>
      </div>
      <div class="fmap-tt__row">
        <span class="fmap-tt__key">Total size</span>
        <span class="fmap-tt__val">${formatSize(node.totalSize)}</span>
      </div>
    </div>
  `;
  tt.style.opacity = "1";
}

function positionTooltip(tt, event, containerRect) {
  const TW = 220;
  const TH = 150;
  const mx = event.clientX - containerRect.left;
  const my = event.clientY - containerRect.top;

  let left = mx + 14;
  let top  = my - 20;

  if (left + TW > containerRect.width  - 8) left = mx - TW - 14;
  if (top  + TH > containerRect.height - 8) top  = my - TH - 10;
  if (top < 4) top = 4;

  tt.style.left = `${left}px`;
  tt.style.top  = `${top}px`;
}

function hideTooltip(tt) {
  tt.style.opacity = "0";
}


function buildFileGraph(container, files, drive, onFileClick) {
  d3.select(container).selectAll("*").remove();
  if (!files || files.length === 0) return;

  const { ownerNode, fileNodes } = computeLayout(files, drive);
  const allNodes = [ownerNode, ...fileNodes];

  const links = fileNodes.map((n) => ({
    source: ownerNode,
    target: n,
  }));

  const tt = createTooltip(container);

  const svg = d3.select(container)
    .append("svg")
    .attr("width",  "100%")
    .attr("height", "100%")
    .attr("viewBox", `0 0 ${W} ${H}`)
    .style("overflow", "hidden")
    .attr("aria-label", "Duplicate file relationship graph");

  const zoomBehaviour = d3.zoom()
    .scaleExtent([0.2, 4])
    .filter((event) => {
      if (event.type === "mousedown")
        return event.target === svg.node() || event.target.tagName === "svg";
      return true;
    })
    .on("zoom", (event) => root.attr("transform", event.transform));

  svg.call(zoomBehaviour);

  container.__zoomIn  = () => svg.transition().duration(280).call(zoomBehaviour.scaleBy, 1.4);
  container.__zoomOut = () => svg.transition().duration(280).call(zoomBehaviour.scaleBy, 0.72);
  container.__reset   = () => svg.transition().duration(380).call(zoomBehaviour.transform, d3.zoomIdentity);

  const defs = svg.append("defs");

  // Generic outward arrow for file edges (coral-ish neutral)
  defs.append("marker")
    .attr("id", "arr-file")
    .attr("viewBox", "0 0 10 10")
    .attr("refX", 9).attr("refY", 5)
    .attr("markerWidth", 6).attr("markerHeight", 6)
    .attr("orient", "auto")
    .append("path")
    .attr("d", "M1 1L9 5L1 9")
    .attr("fill", "none")
    .attr("stroke", "#888780")
    .attr("stroke-width", "1.5")
    .attr("stroke-linecap", "round")
    .attr("stroke-linejoin", "round");

  const root       = svg.append("g").attr("class", "graph-root");
  const edgeLayer  = root.append("g").attr("class", "edges");
  const nodeLayer  = root.append("g").attr("class", "nodes");

  function edgeCoords(link) {
    const sx   = link.source.fx ?? link.source.x;
    const sy   = link.source.fy ?? link.source.y;
    const tx   = link.target.fx ?? link.target.x;
    const ty   = link.target.fy ?? link.target.y;
    const dx   = tx - sx;
    const dy   = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux   = dx / dist;
    const uy   = dy / dist;
    return {
      x1: sx + ux * (link.source.r + 6),
      y1: sy + uy * (link.source.r + 6),
      x2: tx - ux * (link.target.r + 12),
      y2: ty - uy * (link.target.r + 12),
      mx: (sx + tx) / 2,
      my: (sy + ty) / 2,
    };
  }

  const edgeSel = edgeLayer.selectAll("line")
    .data(links)
    .enter()
    .append("line")
    .attr("stroke",       (d) => d.target.fill)
    .attr("stroke-width", 1)
    .attr("opacity",      0.35)
    .attr("fill",         "none")
    .attr("marker-end",   "url(#arr-file)");

  function renderEdges() {
    links.forEach((l) => { l._c = edgeCoords(l); });
    edgeSel
      .attr("x1", (d) => d._c.x1)
      .attr("y1", (d) => d._c.y1)
      .attr("x2", (d) => d._c.x2)
      .attr("y2", (d) => d._c.y2);
  }

  const nodeGroups = nodeLayer.selectAll("g")
    .data(allNodes)
    .enter()
    .append("g")
    .attr("class", "node-g")
    .attr("transform", (d) => `translate(${d.fx},${d.fy})`);

  // Owner dashed ring
  nodeGroups.filter((d) => d.role === "owner")
    .append("circle")
    .attr("r", OWNER_R + 10)
    .attr("fill", "none")
    .attr("stroke", "#534AB7")
    .attr("stroke-width", 1.8)
    .attr("stroke-dasharray", "5 3")
    .attr("opacity", 0.4)
    .attr("class", "node-ring");

  // Main circle
  nodeGroups.append("circle")
    .attr("r",    (d) => d.r)
    .attr("fill", (d) => d.fill)
    .attr("class", "node-main");

  // Label: initials (owner) or first letter (file)
  nodeGroups.append("text")
    .attr("text-anchor",       "middle")
    .attr("dominant-baseline", "central")
    .attr("y",           (d) => d.role === "owner" ? -6 : 0)
    .attr("fill",        "#ffffff")
    .attr("font-size",   (d) => d.role === "owner" ? 16 : 13)
    .attr("font-weight", "500")
    .attr("pointer-events", "none")
    .text((d) => d.label);

  // Owner sub-label: duplicate objects count
  nodeGroups.filter((d) => d.role === "owner")
    .append("text")
    .attr("text-anchor",       "middle")
    .attr("dominant-baseline", "central")
    .attr("y",    11)
    .attr("fill", "rgba(255,255,255,0.65)")
    .attr("font-size", 9)
    .attr("pointer-events", "none")
    .text((d) => `${d.duplicateCount} objects`);

  // Duplicate-count badge on file nodes
  const badgeG = nodeGroups.filter((d) => d.role === "file");

  badgeG.append("circle")
    .attr("cx",           (d) => d.r * 0.72)
    .attr("cy",           (d) => -d.r * 0.72)
    .attr("r",            8)
    .attr("fill",         "#ffffff")
    .attr("stroke",       (d) => d.fill)
    .attr("stroke-width", 0.8);

  badgeG.append("text")
    .attr("x",           (d) => d.r * 0.72)
    .attr("y",           (d) => -d.r * 0.72)
    .attr("text-anchor",       "middle")
    .attr("dominant-baseline", "central")
    .attr("fill",        (d) => d.fill)
    .attr("font-size",   8)
    .attr("font-weight", "600")
    .attr("pointer-events", "none")
    .text((d) => d.duplicateCount);

  nodeGroups.filter((d) => d.role === "file")
    .on("mouseenter", function (event, d) {
      d3.select(this).select(".node-main")
        .transition().duration(130)
        .attr("r", d.r * 1.1)
        .attr("fill", d3.color(d.fill).brighter(0.3));
      const rect = container.getBoundingClientRect();
      showTooltip(tt, d, rect, null);
      positionTooltip(tt, event, rect);
    })
    .on("mousemove", function (event) {
      positionTooltip(tt, event, container.getBoundingClientRect());
    })
    .on("mouseleave", function (_, d) {
      d3.select(this).select(".node-main")
        .transition().duration(130)
        .attr("r", d.r)
        .attr("fill", d.fill);
      hideTooltip(tt);
    });

  // Owner hover
  nodeGroups.filter((d) => d.role === "owner")
    .on("mouseenter", function () {
      d3.select(this).select(".node-main")
        .transition().duration(130)
        .attr("r", OWNER_R * 1.06)
        .attr("fill", d3.color("#534AB7").brighter(0.25));
      d3.select(this).select(".node-ring")
        .transition().duration(130).attr("opacity", 0.7);
    })
    .on("mouseleave", function () {
      d3.select(this).select(".node-main")
        .transition().duration(130)
        .attr("r", OWNER_R).attr("fill", "#534AB7");
      d3.select(this).select(".node-ring")
        .transition().duration(130).attr("opacity", 0.4);
    });

  let dragMoved = false;

  const drag = d3.drag()
    .on("start", function (event) {
      dragMoved = false;
      event.sourceEvent.stopPropagation();
      hideTooltip(tt);
      d3.select(this).raise();
    })
    .on("drag", function (event, d) {
      dragMoved = true;

      if (d.role === "owner") {
        const dx = event.dx;
        const dy = event.dy;
        allNodes.forEach((n) => {
          n.fx = (n.fx ?? n.x) + dx;
          n.fy = (n.fy ?? n.y) + dy;
        });
        nodeGroups.attr("transform", (n) => `translate(${n.fx},${n.fy})`);
      } else {
        d.fx = event.x;
        d.fy = event.y;
        d3.select(this).attr("transform", `translate(${d.fx},${d.fy})`);
      }
      renderEdges();
    })
    .on("end", function (_, d) {
      if (!dragMoved && d.role === "file") {
        onFileClick(toFileRow(d.item));
      }
    });

  nodeGroups.call(drag);

  renderEdges();

  container.__stopSim = () => {};
}


export default function FileMapTab({ duplicateFileData, drive, onFileClick }) {
  const containerRef = useRef(null);

  const handleFileClick = useCallback(
    (row) => { if (typeof onFileClick === "function") onFileClick(row); },
    [onFileClick],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    buildFileGraph(
      containerRef.current,
      duplicateFileData?.content ?? [],
      drive,
      handleFileClick,
    );
    return () => containerRef.current?.__stopSim?.();
  }, [duplicateFileData, drive, handleFileClick]);

  const hasData = (duplicateFileData?.content?.length ?? 0) > 0;

  return (
    <div className="fmap-tab">
      {!hasData ? (
        <div className="fmap-tab__empty">No duplicate file data available</div>
      ) : (
        <>
          <div className="fmap-tab__canvas" ref={containerRef} />

          <div className="fmap-tab__toolbar">
            <div className="fmap-tab__legend">
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot" style={{ background: "#E22D1B" }} /> Critical
              </span>
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot" style={{ background: "#ff2251" }} /> High
              </span>
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot" style={{ background: "#ff8421" }} /> Medium
              </span>
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot" style={{ background: "#081981" }} /> Low
              </span>
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot" style={{ background: "rgba(107, 114, 128, 0.15)" }} /> Pending
              </span>
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot fmap-dot--owner" /> Drive owner
              </span>
              <span className="fmap-tab__legend-hint">
                Node size = duplicate count · Hover to preview · Drag to reposition
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}